import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { DashboardIpad } from './pages/DashboardIpad';
import App from './App';
import { useEffect } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';

// Admin imports
import { AdminLayout } from './admin/layout/AdminLayout';
import { Dashboard as AdminDashboard } from './admin/pages/Dashboard';
import { Analytics as AdminAnalytics } from './admin/pages/Analytics';
import { Users as AdminUsers } from './admin/pages/Users';
import { Wallets as AdminWallets } from './admin/pages/Wallets';
import { Portfolios as AdminPortfolios } from './admin/pages/Portfolios';
import { Modules as AdminModules } from './admin/pages/Modules';
import { IntelligenceWrapper as AdminIntelligence } from './admin/pages/IntelligenceWrapper';
import { Diagnostic as AdminDiagnostic } from './admin/pages/Diagnostic';
import { BlogAdminPage } from './pages/admin/BlogAdminPage';
import { SupportAdmin } from './pages/SupportAdmin';
import { HostingAdmin } from './pages/HostingAdmin';

// Auth flow pages — sit outside the App shell so they're reachable mid-flow
// (e.g. immediately after signup, before email verification completes).
import { EmailVerificationPage } from './pages/auth/EmailVerificationPage';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { PasswordResetRequestPage } from './pages/auth/PasswordResetRequestPage';
import { PasswordResetConfirmPage } from './pages/auth/PasswordResetConfirmPage';

/**
 * AppRouter - Main application router
 *
 * Routes:
 * - /admin/* : Separate admin panel (CDFPI style)
 * - /dashboard-ipad : iPad fullscreen dashboard
 * - /* : Main application
 */
export function AppRouter() {
  const { loadUser, initAuthListeners } = useAuthStore();

  // Load user profile + wire up auth listeners (Supabase events + cross-tab
  // BroadcastChannel) once at app boot. The listener teardown returned by
  // initAuthListeners runs on unmount in case of HMR / future re-renders.
  useEffect(() => {
    loadUser();
    const teardown = initAuthListeners();
    return teardown;
  }, [loadUser, initAuthListeners]);

  return (
    <LanguageProvider>
      <CurrencyProvider>
        <Router>
          <Routes>
            {/* Admin Panel - Completely separate from main app */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="blog" element={<BlogAdminPage />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="wallets" element={<AdminWallets />} />
              <Route path="portfolios" element={<AdminPortfolios />} />
              <Route path="support" element={<SupportAdmin />} />
              <Route path="messages" element={<AdminPlaceholder title="Messages" />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="hosting" element={<HostingAdmin />} />
              <Route path="modules" element={<AdminModules />} />
              <Route path="intelligence" element={<AdminIntelligence />} />
              <Route path="api" element={<AdminPlaceholder title="API & Webhooks" />} />
              <Route path="pages" element={<AdminPlaceholder title="Pages" />} />
              <Route path="medias" element={<AdminPlaceholder title="Medias" />} />
              <Route path="showcase" element={<AdminPlaceholder title="Site vitrine" />} />
              <Route path="showcase/seo" element={<AdminPlaceholder title="SEO" />} />
              <Route path="showcase/analytics" element={<AdminPlaceholder title="Analytics" />} />
              <Route path="settings" element={<AdminPlaceholder title="Paramètres" />} />
              <Route path="billing" element={<AdminPlaceholder title="Facturation" />} />
              <Route path="security" element={<AdminPlaceholder title="Sécurité" />} />
              <Route path="diagnostic" element={<AdminDiagnostic />} />
            </Route>

            {/* iPad Dashboard - Standalone fullscreen route (no AppShell) */}
            <Route
              path="/dashboard-ipad"
              element={
                <QueryClientProvider client={queryClient}>
                  <DashboardIpad />
                </QueryClientProvider>
              }
            />

            {/* Auth flow — must be reachable without the App shell so the
                user can land here right after signup. Wrapped in
                QueryClientProvider for parity with App, in case the page
                ever needs to query Supabase via React Query helpers. The
                login + register pages are dedicated routes (V2 pattern)
                that complement the legacy AuthModal opened from Settings;
                both work in parallel and converge on the same authStore.
                EmailVerificationPage links to /auth/login on the "← Sign in"
                fallback, so wiring it here also fixes the dead link. */}
            <Route
              path="/auth/login"
              element={
                <QueryClientProvider client={queryClient}>
                  <Login />
                </QueryClientProvider>
              }
            />
            <Route
              path="/auth/register"
              element={
                <QueryClientProvider client={queryClient}>
                  <Register />
                </QueryClientProvider>
              }
            />
            <Route
              path="/auth/verify-email"
              element={
                <QueryClientProvider client={queryClient}>
                  <EmailVerificationPage />
                </QueryClientProvider>
              }
            />
            {/* Password reset is two routes:
                - /auth/forgot-password : ask for an email recovery link
                - /auth/reset-password  : land here from the email + set a new
                  password. The path matches the `redirectTo` set in
                  authApi.requestPasswordReset(). */}
            <Route
              path="/auth/forgot-password"
              element={
                <QueryClientProvider client={queryClient}>
                  <PasswordResetRequestPage />
                </QueryClientProvider>
              }
            />
            <Route
              path="/auth/reset-password"
              element={
                <QueryClientProvider client={queryClient}>
                  <PasswordResetConfirmPage />
                </QueryClientProvider>
              }
            />

            {/* Main App (with all tabs, auth modal in Settings) */}
            <Route path="/*" element={<App />} />
          </Routes>
        </Router>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

/**
 * Placeholder component for admin pages not yet implemented
 */
function AdminPlaceholder({ title }: { title: string }) {
  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>
          {title}
        </h1>
        <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
          Cette page est en cours de développement
        </p>
      </div>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '48px',
        textAlign: 'center',
        color: '#94a3b8'
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }}>
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
        <p style={{ fontSize: '1rem', fontWeight: 500, margin: '0 0 8px' }}>
          Page en construction
        </p>
        <p style={{ fontSize: '0.875rem', margin: 0 }}>
          Cette fonctionnalité sera bientôt disponible
        </p>
      </div>
    </div>
  );
}
